import { z } from 'zod'

import { PHONE_REGEXP, REQUIRED_FIELD_MESSAGE } from '@/constants'
import { FORM_VALIDATION_ERROR_MESSAGES } from '@/constants/validation'
import { passwordContainsPhonePart } from '@/utils/auth/passwordValidation'

const PASSWORD_MIN_LENGTH = 6

const passwordSchema = z
  .string({
    required_error: FORM_VALIDATION_ERROR_MESSAGES.REQUIRED_FIELD_MESSAGE,
  })
  .min(1, FORM_VALIDATION_ERROR_MESSAGES.REQUIRED_FIELD_MESSAGE)
  .min(
    PASSWORD_MIN_LENGTH,
    FORM_VALIDATION_ERROR_MESSAGES.PASSWORD_WRONG_FORMAT_MESSAGE
  )

const isPasswordLongEnough = (password: string | undefined) =>
  (password?.length ?? 0) >= PASSWORD_MIN_LENGTH

const passwordsSchema = z
  .object({
    password: passwordSchema,
    repeatPassword: passwordSchema,
  })
  .superRefine(({ password, repeatPassword }, context) => {
    if (
      !isPasswordLongEnough(password) ||
      !isPasswordLongEnough(repeatPassword) ||
      password === repeatPassword
    )
      return

    const message = FORM_VALIDATION_ERROR_MESSAGES.PASSWORDS_MISMATCH_MESSAGE
    context.addIssue({ code: 'custom', message, path: ['password'] })
    context.addIssue({ code: 'custom', message, path: ['repeatPassword'] })
  })

const userFieldsSchema = z.object({
  name: z
    .string({ required_error: REQUIRED_FIELD_MESSAGE })
    .trim()
    .min(1, REQUIRED_FIELD_MESSAGE),
  surname: z
    .string({ required_error: REQUIRED_FIELD_MESSAGE })
    .trim()
    .min(1, REQUIRED_FIELD_MESSAGE),
  phoneNumber: z
    .string({ required_error: REQUIRED_FIELD_MESSAGE })
    .trim()
    .min(1, REQUIRED_FIELD_MESSAGE)
    .regex(PHONE_REGEXP, {
      message: 'Неверный формат телефона',
    }),
  email: z
    .string()
    .trim()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Неправильный адрес электронной почты',
    }),
  roles: z
    .array(z.object({ value: z.string(), label: z.string() }), {
      required_error: FORM_VALIDATION_ERROR_MESSAGES.REQUIRED_FIELD_MESSAGE,
    })
    .min(1, FORM_VALIDATION_ERROR_MESSAGES.REQUIRED_FIELD_MESSAGE),
})

export const userCreationSchema = userFieldsSchema
  .and(passwordsSchema)
  .refine(
    (data) => !passwordContainsPhonePart(data.password, data.phoneNumber),
    {
      message: 'Пароль не должен содержать номер телефона или его часть',
      path: ['password'],
    }
  )

export type UserCreationType = z.infer<typeof userCreationSchema>

export const FORM_FIELDS = [
  {
    id: 1,
    name: 'name',
    title: 'Имя:',
    placeholder: 'Иван',
    required: true,
  },
  {
    id: 2,
    name: 'surname',
    title: 'Фамилия:',
    placeholder: 'Иванов',
    required: true,
  },
  { id: 3, name: 'phoneNumber', title: 'Телефон:', required: true },
  {
    id: 4,
    name: 'email',
    title: 'Почта:',
    placeholder: 'myemail@mail.ru',
    required: false,
    preventAutofill: true,
  },
  {
    id: 5,
    name: 'password',
    title: 'Пароль:',
    placeholder: 'Введите пароль',
    required: true,
  },
  {
    id: 6,
    name: 'repeatPassword',
    title: 'Подтверждение пароля:',
    placeholder: 'Введите пароль еще раз',
    required: true,
  },
]

export const POSSIBlE_CLIENT_ROLES = [
  { value: 'USER', label: 'Юзер' },
  { value: 'SUPERUSER', label: 'Суперюзер' },
]

export const POSSIBLE_PRODUCT_SUPPLIER_ROLES = [
  { value: 'PRODUCT_SUPPLIER_USER', label: 'Пользователь поставщика' },
]

export const POSSIBLE_OWNER_ROLES = [
  { value: 'MANAGER', label: 'Менеджер' },
  { value: 'SALESMAN', label: 'Продажник' },
  { value: 'STOCK_MANAGER', label: 'Менеджер загрузки остатков' },
  { value: 'SUPERMANAGER', label: 'Суперменеджер' },
  { value: 'NOMENCLATURE_EDITOR', label: 'Редактор номенклатуры' },
  { value: 'CATALOG_APPROVER', label: 'Утверждающий каталога' },
  { value: 'INTEGRATION', label: 'Интеграция (УТ)' },
]
